import React, { createContext, useContext, useState, ReactNode } from "react";
import apiClient from "@lift-admin/utils/apiClient";
import { Toastify } from 'lift-ui';
import { actionLog } from "../../App";
import {
  PlaywrightRunRequest,
  requestPlaywrightRun,
} from "./playwright-trigger/playwright-api";

interface ProcessContextType {
  campaignLogs: string[];
  adsetsLogs: string[];
  adsLogs: string[];
  runCampaignProcess: () => Promise<void>;
  runAdsetsProcess: () => Promise<void>;
  runAdsProcess: () => Promise<void>;
  handlePlaywrightTrigger: (request?: PlaywrightRunRequest) => Promise<boolean>;
  handleCRMSync: () => Promise<void>;
  campaignDialogOpen: any;
  setCampaignDialogOpen: any;
  adsetsDialogOpen: any;
  setAdsetsDialogOpen: any;
  adsDialogOpen: any;
  setAdsDialogOpen: any;
  loading:any;
  setLoading:any;
  isError:any;
  setIsError:any;
  isTriggering: boolean;
  setIsTriggering: (triggering: boolean) => void;
  isCRMSyncing: boolean;
  setCRMSyncing: (syncing: boolean) => void;
  crmSyncDialogOpen: boolean;
  setCRMSyncDialogOpen: (open: boolean) => void;
}

const ProcessContext = createContext<ProcessContextType | undefined>(undefined);

interface ProcessProviderProps {
  children: ReactNode;
}

export const ProcessProvider = ({ children }: ProcessProviderProps) => {
  const [campaignLogs, setCampaignLogs] = useState<string[]>([]);
  const [adsetsLogs, setAdsetsLogs] = useState<string[]>([]);
  const [adsLogs, setAdsLogs] = useState<string[]>([]);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [adsetsDialogOpen, setAdsetsDialogOpen] = useState(false);
  const [adsDialogOpen, setAdsDialogOpen] = useState(false);
  const [isTriggering, setIsTriggering] = useState<boolean>(false);
  const [isCRMSyncing, setCRMSyncing] = useState<boolean>(false);
  const [crmSyncDialogOpen, setCRMSyncDialogOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<any>({
    campaigns:false,
    adsets:false,
    ads:false
  })
  const [isError, setIsError] = useState<any>({
    campaigns:false,
    adsets:false,
    ads:false
  })


  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  // Process for Campaigns
  const runCampaignProcess = async () => {
    setIsError((prev: any) => ({ ...prev, campaigns: false }));
    setCampaignLogs([]);
    setCampaignDialogOpen(true);
    setLoading((prev: any) => ({ ...prev, campaigns: true }));
    try {
      // Log trigger start
      await actionLog?.actionLogTrack(JSON.stringify({
        message: "Campaign import process triggered",
        status: 200,
        method: "POST",
        url: "/taximports/api/v1/export/bucket/campaign"
      }), false);

      // Fetch campaign files
      const filesRes = await apiClient.get("/taximports/api/v1/bucket/campaign/files");
      setCampaignLogs((prev) => [...prev, `Fetched bucket campaign files: ${filesRes.data.count} found.`]);
      
      if (filesRes.data.count > 0) {
        // Delete campaign files if found
        const deleteRes = await apiClient.delete("/taximports/api/v1/delete/bucket/campaign?force=true");
        if (deleteRes.data.success && !deleteRes.data.failed_files) {
          setCampaignLogs((prev) => [...prev, deleteRes.data.message || "Deletion successful."]);
        } else {
          throw new Error(deleteRes.data.message || "Delete campaign bucket failed or there are failed files.");
        }
      } else {
        setCampaignLogs((prev) => [...prev, "No files found, skipping deletion."]);
      }
  
      // Export campaign bucket
      const exportRes = await apiClient.post("/taximports/api/v1/export/bucket/campaign");
      const { job_id } = exportRes.data;
      setCampaignLogs((prev) => [...prev, exportRes.data.message || `Export started successfully. Job ID: ${job_id}`]);
  
      // Poll for export status
      let exportStatus: any = null;
      while (true) {
        await delay(3000);
        const statusRes = await apiClient.get(`/taximports/api/v1/export/bucket/status/${job_id}`);
        exportStatus = statusRes.data.status;
        setCampaignLogs((prev) => [...prev, `Export status: ${exportStatus}`]);
        if (exportStatus === "completed" || exportStatus === "error") break;
      }
      if (exportStatus !== "completed") {
        throw new Error("Bucket export encountered an error.");
      }
  
      // Truncate raw_campaigns table
      const truncateRes = await apiClient.post("/taximports/api/v1/truncate/raw_campaigns");
      if (!truncateRes.data.success) {
        throw new Error(truncateRes.data.message || "Truncate raw_campaigns failed.");
      }
      setCampaignLogs((prev) => [...prev, truncateRes.data.message || "Truncate raw_campaigns successful."]);
  
      // Export to Postgres
      const exportPostgresRes = await apiClient.post("/taximports/api/v1/export/postgres/campaign");
      const { task_id } = exportPostgresRes.data;
      setCampaignLogs((prev) => [...prev, exportPostgresRes.data.message || `Postgres export started. Task ID: ${task_id}`]);
  
      // Poll for Postgres export status
      let postgresStatus: any = null;
      while (true) {
        await delay(3000);
        const statusPostgresRes = await apiClient.get(`/taximports/api/v1/export/postgres/status/${task_id}`);
        postgresStatus = statusPostgresRes.data.status;
        setCampaignLogs((prev) => [...prev, `Postgres export status: ${postgresStatus}`]);
        if (postgresStatus === "completed" || postgresStatus === "error") break;
      }
      if (postgresStatus !== "completed") {
        throw new Error("Postgres export encountered an error.");
      }
  
      setCampaignLogs((prev) => [...prev, "Postgres export completed successfully."]);
      Toastify("success", "Campaigns imported successfully");
    } catch (error: any) {
      console.log(error,'error');
      
      const errorMessage =
        error?.response?.data?.detail?.[0]?.msg || 
        error?.response?.data?.detail || 
        error?.message || 
        "An unexpected error occurred.";
  
      Toastify("error", errorMessage);
      setCampaignLogs((prev) => [...prev, "An error occurred: " + (error.message || "Unknown error")]);
      setIsError((prev: any) => ({ ...prev, campaigns: true }));
      
      await actionLog?.actionLogTrack(JSON.stringify({
        message: `Campaign import process failed: ${errorMessage}`,
        status: error?.response?.status || 500,
        method: "POST",
        url: "/taximports/api/v1/export/postgres/campaign"
      }), true);
    } finally {
      setLoading((prev: any) => ({ ...prev, campaigns: false }));
    }
  };
  

  // Process for Adsets
  const runAdsetsProcess = async () => {
    setIsError((prev: any) => ({ ...prev, adsets: false }));
    setAdsetsLogs([]);
    setAdsetsDialogOpen(true);
    setLoading((prev: any) => ({ ...prev, adsets: true }));
  
    try {
      // Log trigger start
      await actionLog?.actionLogTrack(JSON.stringify({
        message: "Adset import process triggered",
        status: 200,
        method: "POST",
        url: "/taximports/api/v1/export/bucket/adset"
      }), false);

      // Fetch adset files
      const filesRes = await apiClient.get("/taximports/api/v1/bucket/adset/files");
      setAdsetsLogs((prev) => [...prev, `Fetched bucket adset files: ${filesRes.data.count} found.`]);
      
      if (filesRes.data.count > 0) {
        // Delete adset files if found
        const deleteRes = await apiClient.delete("/taximports/api/v1/delete/bucket/adset?force=true");
        if (deleteRes.data.success && !deleteRes.data.failed_files) {
          setAdsetsLogs((prev) => [...prev, deleteRes.data.message || "Deletion successful."]);
        } else {
          throw new Error(deleteRes.data.message || "Delete adset bucket failed or there are failed files.");
        }
      } else {
        setAdsetsLogs((prev) => [...prev, "No files found, skipping deletion."]);
      }
  
      // Export adset bucket
      const exportRes = await apiClient.post("/taximports/api/v1/export/bucket/adset");
      const { job_id } = exportRes.data;
      setAdsetsLogs((prev) => [...prev, exportRes.data.message || `Export started successfully. Job ID: ${job_id}`]);
  
      // Poll for export status
      let exportStatus: any = null;
      while (true) {
        await delay(3000);
        const statusRes = await apiClient.get(`/taximports/api/v1/export/bucket/status/${job_id}`);
        exportStatus = statusRes.data.status;
        setAdsetsLogs((prev) => [...prev, `Export status: ${exportStatus}`]);
        if (exportStatus === "completed" || exportStatus === "error") break;
      }
      if (exportStatus !== "completed") {
        throw new Error("Bucket export encountered an error.");
      }
  
      // Truncate raw_adsets table
      const truncateRes = await apiClient.post("/taximports/api/v1/truncate/raw_adsets");
      if (!truncateRes.data.success) {
        throw new Error(truncateRes.data.message || "Truncate raw_adsets failed.");
      }
      setAdsetsLogs((prev) => [...prev, truncateRes.data.message || "Truncate raw_adsets successful."]);
  
      // Export to Postgres
      const exportPostgresRes = await apiClient.post("/taximports/api/v1/export/postgres/adset");
      const { task_id } = exportPostgresRes.data;
      setAdsetsLogs((prev) => [...prev, exportPostgresRes.data.message || `Postgres export started. Task ID: ${task_id}`]);
  
      // Poll for Postgres export status
      let postgresStatus: any = null;
      while (true) {
        await delay(3000);
        const statusPostgresRes = await apiClient.get(`/taximports/api/v1/export/postgres/status/${task_id}`);
        postgresStatus = statusPostgresRes.data.status;
        setAdsetsLogs((prev) => [...prev, `Postgres export status: ${postgresStatus}`]);
        if (postgresStatus === "completed" || postgresStatus === "error") break;
      }
      if (postgresStatus !== "completed") {
        throw new Error("Postgres export encountered an error.");
      }
  
      setAdsetsLogs((prev) => [...prev, "Postgres export completed successfully."]);
      Toastify("success", "Adsets imported successfully");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail?.[0]?.msg || 
        error?.response?.data?.detail || 
        error?.message || 
        "An unexpected error occurred.";
  
      Toastify("error", errorMessage);
      setAdsetsLogs((prev) => [...prev, "An error occurred: " + (error.message || "Unknown error")]);
      setIsError((prev: any) => ({ ...prev, adsets: true }));
      
      await actionLog?.actionLogTrack(JSON.stringify({
        message: `Adset import process failed: ${errorMessage}`,
        status: error?.response?.status || 500,
        method: "POST",
        url: "/taximports/api/v1/export/postgres/adset"
      }), true);
    } finally {
      setLoading((prev: any) => ({ ...prev, adsets: false }));
    }
  };
  

  // Process for Ads
  const runAdsProcess = async () => {
    setIsError((prev: any) => ({ ...prev, ads: false }));
    setAdsLogs([]);
    setAdsDialogOpen(true);
    setLoading((prev: any) => ({ ...prev, ads: true }));
  
    try {
      // Log trigger start
      await actionLog?.actionLogTrack(JSON.stringify({
        message: "Ad import process triggered",
        status: 200,
        method: "POST",
        url: "/taximports/api/v1/export/bucket/ad"
      }), false);

      // Fetch ad files
      const filesRes = await apiClient.get("/taximports/api/v1/bucket/ad/files");
      setAdsLogs((prev) => [
        ...prev,
        `Fetched bucket ad files: ${filesRes.data.count} found.`,
      ]);
      
      if (filesRes.data.count > 0) {
        // Delete ad files if found
        const deleteRes = await apiClient.delete("/taximports/api/v1/delete/bucket/ad?force=true");
        if (deleteRes.data.success && !deleteRes.data.failed_files) {
          setAdsLogs((prev) => [...prev, deleteRes.data.message || "Deletion successful."]);
        } else {
          throw new Error(deleteRes.data.message || "Delete ad bucket failed or there are failed files.");
        }
      } else {
        setAdsLogs((prev) => [...prev, "No files found, skipping deletion."]);
      }
  
      // Export ad bucket
      const exportRes = await apiClient.post("/taximports/api/v1/export/bucket/ad");
      const { job_id } = exportRes.data;
      setAdsLogs((prev) => [
        ...prev,
        exportRes.data.message || `Export started successfully. Job ID: ${job_id}`,
      ]);
  
      // Poll for export status
      let exportStatus: any = null;
      while (true) {
        await delay(3000);
        const statusRes = await apiClient.get(`/taximports/api/v1/export/bucket/status/${job_id}`);
        exportStatus = statusRes.data.status;
        setAdsLogs((prev) => [...prev, `Export status: ${exportStatus}`]);
        if (exportStatus === "completed" || exportStatus === "error") break;
      }
      if (exportStatus !== "completed") {
        throw new Error("Bucket export encountered an error.");
      }
  
      // Truncate raw_ads table
      const truncateRes = await apiClient.post("/taximports/api/v1/truncate/raw_ads");
      if (!truncateRes.data.success) {
        throw new Error(truncateRes.data.message || "Truncate raw_ads failed.");
      }
      setAdsLogs((prev) => [...prev, truncateRes.data.message || "Truncate raw_ads successful."]);
  
      // Export to Postgres
      const exportPostgresRes = await apiClient.post("/taximports/api/v1/export/postgres/ad");
      const { task_id } = exportPostgresRes.data;
      setAdsLogs((prev) => [
        ...prev,
        exportPostgresRes.data.message || `Postgres export started. Task ID: ${task_id}`,
      ]);
  
      // Poll for Postgres export status
      let postgresStatus: any = null;
      while (true) {
        await delay(3000);
        const statusPostgresRes = await apiClient.get(`/taximports/api/v1/export/postgres/status/${task_id}`);
        postgresStatus = statusPostgresRes.data.status;
        setAdsLogs((prev) => [...prev, `Postgres export status: ${postgresStatus}`]);
        if (postgresStatus === "completed" || postgresStatus === "error") break;
      }
      if (postgresStatus !== "completed") {
        throw new Error("Postgres export encountered an error.");
      }
  
      setAdsLogs((prev) => [...prev, "Postgres export completed successfully."]);
      Toastify("success", "Ads imported successfully");
  
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail?.[0]?.msg || 
        error?.response?.data?.detail || 
        error?.message || 
        "An unexpected error occurred.";
  
      Toastify("error", errorMessage);
      setAdsLogs((prev) => [...prev, `An error occurred: ${errorMessage}`]);
      setIsError((prev: any) => ({ ...prev, ads: true }));
      
      await actionLog?.actionLogTrack(JSON.stringify({
        message: `Ad import process failed: ${errorMessage}`,
        status: error?.response?.status || 500,
        method: "POST",
        url: "/taximports/api/v1/export/postgres/ad"
      }), true);
    } finally {
      setLoading((prev: any) => ({ ...prev, ads: false }));
    }
  };
  
  const handlePlaywrightTrigger = async (
    request: PlaywrightRunRequest = { environment: "dev" }
  ) => {
    if (isTriggering) return false;
    setIsTriggering(true);
    try {
      const response = await requestPlaywrightRun(request);
      sessionStorage.setItem("playwright:lastRunId", response.message_id);
      Toastify("success", "queued");
      try {
        await actionLog?.actionLogTrack(JSON.stringify({
          message: `Playwright test run queued (${response.message_id})`,
          status: 202,
          method: "POST",
          url: "/event-ingress/events/playwright-tests"
        }), false);
      } catch {
        // Audit logging must not change an accepted trigger into a UI failure.
      }
      return true;
    } catch (error: any) {
      Toastify("error", error?.response?.data?.message || "Something went wrong");
      await actionLog?.actionLogTrack(JSON.stringify({
        message: `Playwright Trigger failed: ${error?.response?.data?.message}`,
        status: error?.response?.status || 500,
        method: "POST",
        url: "/event-ingress/events/playwright-tests"
      }), true);
      return false;
    } finally {
      setIsTriggering(false);
    }
  };
  
  const handleCRMSync = async () => {
    if (isCRMSyncing) {
      setCRMSyncDialogOpen(true);
      return;
    }

    setCRMSyncing(true);
    try {
      const res = await apiClient.post(
        "/crm/v1/sync/sync-cards",
        {}
      );
      console.log(res, "res");
      Toastify("success", "CRM Sync completed successfully");
      await actionLog?.actionLogTrack(JSON.stringify({
        message: "CRM Sync process executed",
        status: 200,
        method: "POST",
        url: "/crm/v1/sync/sync-cards"
      }), false);
    } catch (error: any) {
      console.log(error, "error");
      Toastify("error", error?.response?.data?.message || "Something went wrong");
      await actionLog?.actionLogTrack(JSON.stringify({
        message: `CRM Sync failed: ${error?.response?.data?.message}`,
        status: error?.response?.status || 500,
        method: "POST",
        url: "/crm/v1/sync/sync-cards"
      }), true);
    } finally {
      setCRMSyncing(false);
    }
  };

  return (
    <ProcessContext.Provider
      value={{
        campaignLogs,
        adsetsLogs,
        adsLogs,
        runCampaignProcess,
        runAdsetsProcess,
        runAdsProcess,
        handlePlaywrightTrigger,
        handleCRMSync,
        campaignDialogOpen,
        setCampaignDialogOpen,
        adsetsDialogOpen,
        setAdsetsDialogOpen,
        adsDialogOpen,
        setAdsDialogOpen,
        loading,
        setLoading,
        isError,
        setIsError,
        isTriggering,
        setIsTriggering,
        isCRMSyncing,
        setCRMSyncing,
        crmSyncDialogOpen,
        setCRMSyncDialogOpen,
      }}
    >
      {children}
    </ProcessContext.Provider>
  );
};

export const useProcessContext = () => {
  const context = useContext(ProcessContext);
  if (!context) {
    throw new Error("useProcessContext must be used within a ProcessProvider");
  }
  return context;
};